import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PartnerDealBriefingSectionResponse,
  PartnerDealInvestigationProgress,
} from '@interfaces/partners/responses/partner-deal-briefing.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  investigationElapsedLabel,
  investigationSteps,
} from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-briefing-tab/briefing-presenter';
import { interval } from 'rxjs';

/** Intervalo do relógio local do "Começou há N min" (não chama a API: o progresso chega pelo tempo real + GET). */
const ELAPSED_TICK_MS = 30_000;

/**
 * Andamento da investigação em curso na caixa da aba Briefing (card 3918): as quatro etapas (Cadastro e sócios na
 * Receita, Varredura da internet, Aprofundamento, Conclusão e red flags) com ícone de feita, atual ou pendente, e
 * embaixo a última atividade do agente e o tempo desde o início. A derivação das etapas é a função pura
 * `investigationSteps` do presenter; aqui só o relógio do tempo decorrido.
 */
@Component({
  selector: 'app-briefing-investigation-progress',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './briefing-investigation-progress.component.html',
  styleUrl: './briefing-investigation-progress.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingInvestigationProgressComponent {
  private readonly dateFormatter = inject(DateFormatterService);

  /** Progresso da investigação que roda (GET + tempo real); nulo antes da primeira notícia. */
  readonly progress = input<PartnerDealInvestigationProgress | null>(null);
  /** Catálogo do briefing, para o nome de negócio das subseções `investigation_*`. */
  readonly catalog = input<readonly PartnerDealBriefingSectionResponse[]>([]);
  /** Na fila: todas as etapas pendentes. */
  readonly queued = input(false);

  private readonly now = signal(this.dateFormatter.getNowTimestamp());

  protected readonly steps = computed(() =>
    investigationSteps(this.progress(), this.catalog(), this.queued())
  );
  protected readonly activity = computed(() => this.progress()?.activity?.trim() || null);
  protected readonly elapsed = computed(() => {
    const startedAt = this.dateFormatter.parseDate(this.progress()?.startedAt ?? null);
    return investigationElapsedLabel(startedAt ? startedAt.getTime() : null, this.now());
  });

  constructor() {
    interval(ELAPSED_TICK_MS)
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.now.set(this.dateFormatter.getNowTimestamp()));
    // Progresso novo acerta o relógio na hora, sem esperar o próximo tique.
    effect(() => {
      this.progress();
      untracked(() => this.now.set(this.dateFormatter.getNowTimestamp()));
    });
  }
}
